import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CircleCheck, Lock } from "lucide-react";

import { certificationPaths } from "@/domains/certifications/routes";
import {
  formatCertificationDate,
  getModuleVideoMeta,
} from "@/domains/certifications/utils";
import { WeekRowProps } from "@/features/certifications/overview/components/types";
import { getCompletedWeekMeta } from "@/features/certifications/overview/utils";
import { MODULE_STATUSES } from "@/domains/certifications/constants";

import "./styles.css";

const CHECK_ICON_SIZE = 20;

const WeekRow = ({ slug, week, showUnlockReason, opensAt }: WeekRowProps) => {
  const { t } = useTranslation();
  const weekLabel = t("certification.overview.weekLabel", {
    position: week.position,
  });

  if (week.status === MODULE_STATUSES.LOCKED) {
    const unlockReason = opensAt
      ? t("certification.overview.opensOn", {
          date: formatCertificationDate(opensAt),
        })
      : t("certification.overview.unlockReason", {
          position: week.position - 1,
        });
    const meta = showUnlockReason
      ? [getModuleVideoMeta(t, week.module), unlockReason].join(" · ")
      : getModuleVideoMeta(t, week.module);

    return (
      <li className="week-row week-row-locked flex items-center gap-400">
        <span className="week-row-label flex-shrink-0 body-xs-regular">
          {weekLabel}
        </span>

        <div className="flex-1 flex flex-col gap-50">
          <span className="week-row-title body-m-regular">
            {week.module.title}
          </span>

          <span className="week-row-meta body-xs-regular">{meta}</span>
        </div>

        <span className="week-row-locked-label flex items-center gap-100 body-xs-regular">
          <Lock size={12} aria-hidden />
          {t("certification.overview.locked")}
        </span>
      </li>
    );
  }

  const isComplete = week.status === MODULE_STATUSES.COMPLETE;

  return (
    <li>
      <Link
        to={certificationPaths.module(slug, week.module.id)}
        className="week-row week-row-link flex items-center gap-400"
      >
        {isComplete && (
          <CircleCheck
            size={CHECK_ICON_SIZE}
            className="week-row-check flex-shrink-0"
            aria-hidden
          />
        )}

        <span className="week-row-label flex-shrink-0 body-xs-regular">
          {weekLabel}
        </span>

        <div className="flex-1 flex flex-col gap-50">
          <span className="week-row-title body-m-medium">
            {week.module.title}
          </span>

          <span className="week-row-meta body-xs-regular">
            {isComplete
              ? getCompletedWeekMeta(t, week)
              : getModuleVideoMeta(t, week.module)}
          </span>
        </div>

        {isComplete && (
          <span className="week-row-complete body-xs-regular">
            {t("certification.overview.complete")}
          </span>
        )}

        <span className="week-row-action body-xs-regular">
          {isComplete
            ? t("certification.overview.revisit")
            : t("certification.overview.open")}
        </span>
      </Link>
    </li>
  );
};

export default WeekRow;
